import type { DailyLog, WeeklyLog, Lab, SavingsGoal, SavingsSnapshot, BudgetItem } from "./types";

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: "same-origin",
    headers: {
      "content-type": "application/json",
      ...(init?.headers ?? {}),
    },
  });
  if (res.status === 401) throw new AuthError();
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}: ${await res.text()}`);
  if (res.status === 204) return undefined as T;
  return res.json();
}

export class AuthError extends Error {
  constructor() { super("unauthorized"); this.name = "AuthError"; }
}

export const api = {
  me: () => req<{ email: string | null }>("/api/me"),
  login: (email: string, password: string) =>
    req<{ email: string }>("/api/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }),
  logout: () => req<{ ok: true }>("/api/auth/logout", { method: "POST" }),

  dailyList: (sinceDays = 90) => req<DailyLog[]>(`/api/daily-logs?since=${sinceDays}`),
  dailyGet: (date: string) => req<DailyLog | null>(`/api/daily-logs/${date}`),
  dailyUpsert: (log: DailyLog) =>
    req<DailyLog>("/api/daily-logs", { method: "POST", body: JSON.stringify(log) }),

  weeklyList: () => req<WeeklyLog[]>("/api/weekly-logs"),
  weeklyUpsert: (log: WeeklyLog) =>
    req<WeeklyLog>("/api/weekly-logs", { method: "POST", body: JSON.stringify(log) }),

  labsList: () => req<Lab[]>("/api/labs"),
  labCreate: (lab: Omit<Lab, "id">) =>
    req<Lab>("/api/labs", { method: "POST", body: JSON.stringify(lab) }),

  goalsList: () => req<SavingsGoal[]>("/api/savings-goals"),
  goalCreate: (g: Omit<SavingsGoal, "id">) =>
    req<SavingsGoal>("/api/savings-goals", { method: "POST", body: JSON.stringify(g) }),
  goalUpdate: (id: string, patch: Partial<SavingsGoal>) =>
    req<SavingsGoal>(`/api/savings-goals/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),

  snapshotsList: () => req<SavingsSnapshot[]>("/api/savings-snapshots"),
  snapshotCreate: (s: Omit<SavingsSnapshot, "id">) =>
    req<SavingsSnapshot>("/api/savings-snapshots", { method: "POST", body: JSON.stringify(s) }),

  budgetList: () => req<BudgetItem[]>("/api/budget-items"),
  budgetCreate: (item: Omit<BudgetItem, "id" | "active"> & { active?: number }) =>
    req<BudgetItem>("/api/budget-items", { method: "POST", body: JSON.stringify(item) }),
  budgetUpdate: (id: string, patch: Partial<BudgetItem>) =>
    req<BudgetItem>(`/api/budget-items/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
  budgetDelete: (id: string) =>
    req<void>(`/api/budget-items/${id}`, { method: "DELETE" }),
};
